import { useMemo } from 'react';
import { Download } from 'lucide-react';
import {
  type IncomingDocLineDto,
} from '@eombor/contracts';
import { Badge, Button, Dialog, Skeleton, Table, cn, type Column } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import { useIncomingDoc } from '../model/accounting.queries.js';
import { formatDate, qtyText } from '../model/format.js';
import { unitText } from '../../../shared/i18n/labels.js';

export interface DocPreviewDialogProps {
  /** `null` — oyna yopiq. Hujjat FAQAT ochilganda so'raladi. */
  docId: string | null;
  onClose: () => void;
  /** PDF yuklab olish — sahifa o'zi bajaradi (bildirishnoma ham o'sha yerda). */
  onDownloadPdf?: (docNo: string) => void;
  pdfPending?: boolean;
}

interface FactProps {
  label: string;
  children: React.ReactNode;
}

const Fact = ({ label, children }: FactProps) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-xs text-text-faint">{label}</dt>
    <dd className="min-w-0 text-sm text-text">{children}</dd>
  </div>
);

/**
 * Prixodning tez ko'rinishi.
 *
 * Satrlar RO'YXATDA kelmaydi (`IncomingDocRow` da ular yo'q) — shuning uchun
 * hujjat aynan shu oyna ochilganda so'raladi. Hisobchi ko'pincha "shu
 * hujjatda nima bor?" degan savolga javob olish uchun ochadi va shu savol
 * uchun butun sahifani almashtirish ortiqcha bo'lardi.
 */
export const DocPreviewDialog = ({
  docId,
  onClose,
  onDownloadPdf,
  pdfPending = false,
}: DocPreviewDialogProps) => {
  const doc = useIncomingDoc(docId);
  const nomenclatures = useNomenclatures({ limit: 200 });
  const warehouses = useWarehouses({ limit: 200 });

  const nomenclatureOf = useMemo(
    () => new Map((nomenclatures.data?.items ?? []).map((row) => [row.id, row])),
    [nomenclatures.data],
  );

  const columns: Column<IncomingDocLineDto>[] = [
    { key: 'lineNo', header: '№', mono: true, width: '3rem' },
    {
      key: 'nomenclatureId',
      header: t('accounting.colNomenclature'),
      render: (line) => {
        const nomenclature = nomenclatureOf.get(line.nomenclatureId);
        return (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-text">
              {nomenclature?.name ?? `#${line.nomenclatureId}`}
            </span>
            {nomenclature === undefined ? null : (
              <span className="tabular text-xs text-text-faint">{nomenclature.code}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'qty',
      header: t('accounting.colQty'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (line) => {
        const unit = nomenclatureOf.get(line.nomenclatureId)?.unit;
        return `${qtyText(line.qty)}${unit === undefined ? '' : ` ${unitText(unit)}`}`;
      },
    },
    {
      key: 'batchNo',
      header: t('accounting.colBatch'),
      mono: true,
      width: '10rem',
      // Partiya raqami hujjatga NUSXA bo'lib tushgan: partiya keyin
      // o'zgarsa ham, qog'ozdagi raqam o'zgarmaydi.
      render: (line) =>
        line.batchNo ?? <span className="text-text-faint">{t('accounting.noBatch')}</span>,
    },
  ];

  return (
    <Dialog
      open={docId !== null}
      onClose={onClose}
      title={t('accounting.previewTitle')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('accounting.close')}
          </Button>
          {doc.data === undefined || onDownloadPdf === undefined ? null : (
            <Button loading={pdfPending} onClick={() => onDownloadPdf(doc.data.docNo)}>
              <Download size={14} aria-hidden />
              {t('accounting.downloadPdf')}
            </Button>
          )}
        </>
      }
    >
      {doc.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : doc.isError || doc.data === undefined ? (
        <p
          role="alert"
          className={cn(
            'rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft',
            'px-3 py-2 text-sm text-danger',
          )}
        >
          {t('accounting.previewError')}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="tabular text-lg font-semibold tracking-tight text-text">
              {doc.data.docNo}
            </span>
            {doc.data.externalRef === null ? (
              <Badge tone="neutral">{t('accounting.notSentTo1c')}</Badge>
            ) : (
              <Badge tone="success" mono>
                {doc.data.externalRef}
              </Badge>
            )}
          </div>

          <dl className="grid gap-3 sm:grid-cols-3">
            <Fact label={t('accounting.fieldRequisition')}>
              <span className="tabular">{doc.data.requisitionDocNo}</span>
            </Fact>
            <Fact label={t('accounting.colDate')}>
              <span className="tabular">{formatDate(doc.data.createdAt) ?? '—'}</span>
            </Fact>
            <Fact label={t('accounting.fieldWarehouse')}>
              {(warehouses.data?.items ?? []).find((row) => row.id === doc.data.warehouseId)
                ?.name ?? `#${doc.data.warehouseId}`}
            </Fact>
          </dl>

          <Table
            caption={t('accounting.previewCaption')}
            columns={columns}
            rows={doc.data.lines}
            rowKey={(line) => line.id}
          />
        </div>
      )}
    </Dialog>
  );
};
